import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import BaseService from "services/BaseService"; // QPM 추적을 위한 BaseService 추가
// 정확한 import 경로 확인
import { setUser } from "store/auth/userSlice"; // userSlice 정확한 경로로 수정
import { isMaintenanceActive } from "services/maintenance";

const REACT_APP_BE_API = process.env.REACT_APP_BE_API || '/qcore/home';

const SessionKeeper = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state);
  // auth/user 슬라이스의 정확한 경로 확인
  const user = state?.auth?.user || {};
  const { userId } = user;

  // interval ID를 저장하기 위한 ref
  const intervalRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const failureCountRef = useRef(0); // 연속 실패 횟수 추적
  const lastKeepAliveTimeRef = useRef(Date.now()); // 마지막 세션 유지 시간

  // 세션 유지 API 호출 함수
  const keepSessionAlive = useCallback(async () => {
    // userId가 없으면 즉시 중단
    if (!userId) {
      return;
    }

    try {
      // BaseService를 사용하여 QPM 추적 활성화
      await BaseService.get(`${REACT_APP_BE_API}/qbase/api/keep-session`, {
        withCredentials: true,
        timeout: 10000, // 10초 타임아웃
        baseURL: ''  // baseURL 무시
      });

      // 성공 시 실패 카운트 리셋 및 마지막 호출 시간 업데이트
      failureCountRef.current = 0;
      lastKeepAliveTimeRef.current = Date.now();
    } catch (error) {
      console.error("세션 유지 API 호출 실패:", error.message);

      // 큐베이스 점검 중(쓰기 동결·DB 전환 재기동)의 연결 실패는 세션 만료가 아니다 — 로그아웃하지 않고 다음 주기에 다시 확인
      // (401/403 은 점검과 무관한 실제 인증 실패이므로 아래 처리 그대로)
      const authStatus = error.response && (error.response.status === 401 || error.response.status === 403);
      if (isMaintenanceActive() && !authStatus) {
        failureCountRef.current = 0;
        return;
      }

      // 실패 횟수 증가
      failureCountRef.current += 1;

      // 인증 에러(401, 403) 또는 네트워크 에러인 경우
      if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
          // 401: 세션 만료 (Unauthorized)
          // 403: 권한 없음 (Forbidden) - 세션이 유효하지 않거나 권한이 없음
          console.warn(`인증 실패 (${error.response.status}): 세션이 만료되었거나 권한이 없습니다. 로그아웃 처리 중...`);

          // 모든 interval 정리
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          // setUser를 사용해 초기 상태로 리셋 (로그아웃)
          dispatch(setUser({
            name: '',
            userName: '',
            email: '',
            userId: '',
            authority: [],
            avatar: ''
          }));

          // 로그인 페이지로 리디렉션
          window.location.href = '/sign-in';
        }
      } else {
        // 네트워크 오류 - 3회 연속 실패 시 로그아웃
        console.warn(`세션 유지 요청 실패 (네트워크 오류, ${failureCountRef.current}회): ${error.message}`);

        if (failureCountRef.current >= 3) {
          console.error('세션 유지 연속 3회 실패 - 세션 만료로 간주하여 로그아웃 처리');

          // 모든 interval 정리
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          // setUser를 사용해 초기 상태로 리셋 (로그아웃)
          dispatch(setUser({
            name: '',
            userName: '',
            email: '',
            userId: '',
            authority: [],
            avatar: ''
          }));

          // 로그인 페이지로 리디렉션
          window.location.href = '/sign-in';
        }
      }
    }
  }, [dispatch, userId]);

  // 세션 유지 interval 설정 및 네트워크 상태 관리
  useEffect(() => {
    // userId가 없으면 실행하지 않음
    if (!userId) {
      // 기존 interval이 있다면 정리
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }


    // 네트워크 상태 이벤트 핸들러
    const handleOnline = () => {
      if (!userId) return; // userId 재확인
      keepSessionAlive();

      // 기존 interval 정리 후 새로 설정
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(keepSessionAlive, 300000); // 5분
    };

    const handleOffline = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // 네트워크 이벤트 리스너 등록
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // 초기 실행 및 interval 설정 - 약간의 지연 후 시작
    const initialTimeout = setTimeout(() => {
      if (userId) { // userId 다시 확인
        keepSessionAlive(); // 즉시 첫 호출
        intervalRef.current = setInterval(keepSessionAlive, 300000); // 5분마다
      }
    }, 1000); // 1초 후 시작

    // 정리 함수
    return () => {
      clearTimeout(initialTimeout);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

    };
  }, [keepSessionAlive, userId]);
  
  // 사용자 활동 감지를 통한 세션 자동 갱신
  useEffect(() => {
    if (!userId) return;
    
    const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    
    // 사용자 활동 감지 시 호출될 함수
    const handleUserActivity = () => {
      // 기존 timeout 제거
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      
      // 20분 비활성 후 활동 감지 시에만 세션 갱신 (서버 부하 방지)
      inactivityTimeoutRef.current = setTimeout(() => {
        keepSessionAlive();
      }, 1200000); // 20분
    };
    
    // 활동 이벤트 리스너 등록
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });
    
    // 초기 timeout 설정
    inactivityTimeoutRef.current = setTimeout(() => {}, 1200000);
    
    // 정리 함수
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [keepSessionAlive, userId]);

  // 슬립 복귀 감지 (visibilitychange 이벤트)
  // PC가 슬립모드에서 깨어나면 브라우저 탭이 visible 상태로 전환됨
  useEffect(() => {
    if (!userId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const elapsed = now - lastKeepAliveTimeRef.current;

        // 세션 유지 간격(5분) 이상 경과했으면 즉시 세션 확인
        if (elapsed >= 300000) {
          keepSessionAlive();

          // interval 재시작 (슬립 중 interval이 정확하지 않을 수 있음)
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          intervalRef.current = setInterval(keepSessionAlive, 300000);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [keepSessionAlive, userId]);

  // UI를 렌더링하지 않음
  return null;
};

export default SessionKeeper;